import { useState, useCallback } from 'react';
import useDeal from './useDeal.js';
import { tokenForItem, toastNotice, TOKEN_FAILED_WORDS } from '@/services/session-token.service.js';
import { toast } from '@/components/ui/toaster.jsx';

// Shared action-and-sheet wiring for any surface that wants to act on
// a deal — currently used by:
//   - sheets/DealActionBar (account workspace footer)
//   - pipeline/PipelineDealCard (kanban card ⋯ menu — fix #1, 2026-05-20)
//
// Both surfaces need: stage-aware visibility of actions + a way to
// open the right sheet/modal when an action fires. This hook owns the
// open-state for each sheet and returns:
//
//   { stages, actions, derivedStage, crmStage, sessionToken,
//     dealContext, openers, sheetState }
//
// `actions` is the array of {key, label, Icon} entries that are
// visible for the current stage (per the same HIDE_ON_STAGE map the
// old DealActionBar carried). `openers[key]()` opens the corresponding
// sheet. `sheetState` is a serialized snapshot (which sheet is open,
// which status variant for StatusTransitionSheet) so consumers can
// render exactly one set of sheets.

import {
  FileX,
  FilePlus2,
  Send,
  RefreshCw,
  PenLine,
  FileSignature,
  Receipt,
  Plane,
  FileSearch,
  FolderCheck,
  ShieldCheck,
  FileUp,
  UserPlus,
  MessageSquare,
  Link2,
  Mail,
  Replace,
  PauseCircle,
} from 'lucide-react';

// 2026-05-20 (Vision §V2 fix #6): the prior 'fullQuote' action was
// dropped when Quick Log + Full Quote merged into a single
// QuoteEntrySheet. "Log quote" now opens the one progressive-disclosure
// sheet — depth is determined by which sections the broker expands,
// not by which menu item they picked.
export const ALL_ACTIONS = [
  { key: 'send', label: 'Send', Icon: Send, alwaysVisible: true },
  { key: 'decline', label: 'Log decline', Icon: FileX },
  { key: 'quote', label: 'Log quote', Icon: FilePlus2 },
  // 'Mark presented' / 'Mark paid' / 'Mark bound' are GONE (DEAL-LIFECYCLE-
  // SPEC Phase 1): lifecycle state is derived from ground truth — presenting
  // a quote, taking payment, and recording placement move the deal; there is
  // no manual stage door. Terminal moves (Lost/Cancelled/Expired + reason)
  // live on the stage badge.
  // "Initiate renewal" — only shown for Bound deals approaching
  // expiration (stage RENEWAL_DUE = Bound + within 100d of Policy_End +
  // no successor). Visibility handled by SHOW_ON_STAGE below (the
  // opposite shape of HIDE_ON_STAGE because this is a single-stage
  // action — listing every excluded stage would be noise).
  { key: 'renew', label: 'Initiate renewal', Icon: RefreshCw },
  // Intake outreach (MV-01 3.4/3.5, Ship 1b): the broker UIs for the 1a
  // welcome-text + resume-link endpoints. Pre-shop deals only (STATE_ACTIONS);
  // they also live at intake wrap-up inside the broker-mode wizard.
  { key: 'welcome_text', label: 'Send welcome text', Icon: MessageSquare },
  { key: 'resume_link', label: 'Send resume link', Icon: Link2 },
  // RTQ dispatch (MV-02 2.4, Ship 1b): the existing RTQ email rendered from
  // current deal data + a required hand-typed blurb, one email per recipient,
  // frozen snapshot + submission_markets row at 'sent'. Pre-shop states.
  { key: 'rtq', label: 'Send to markets', Icon: Mail },
  // SNKL is available on any Deal regardless of stage — carriers ask for it
  // at bind, mid-term, and renewal. Spec: BR-AND-SNKL-TOWER-SPEC.md §3.5.
  // 'binding' REMOVED (193 §4). David: "the signature should be collected during
  // the binding wizard. It's a wasted button entirely... that shouldn't even be
  // a top level thing. It's a servicing tool."
  //
  // It was worse than a mislabel. On an ordinary aviation deal the sheet is not
  // even mounted (DealActionsSheets only renders the BR/CA request dialogs), so
  // in `binding` lifecycle state this was the ACCENT PRIMARY button and clicking
  // it did nothing at all. It also named the wrong document: it sends the
  // binding APPLICATION, the form a client signs to request coverage, not a
  // binder.
  //
  // WHAT SURVIVES AND WHAT DOES NOT, measured rather than assumed:
  //   Builders Risk  KEEPS its door. BrStatusPanel mounts its own
  //                  BrRequestDialog (BrStatusPanel.jsx:371) and the panel is
  //                  rendered on BR deals (DealWorkspace.jsx:1037).
  //   Commercial Av. LOSES it. CaRequestDialog was reachable ONLY through this
  //                  action. CaStatusPanel exists and mounts one
  //                  (CaStatusPanel.jsx:104) but has ZERO importers, so that
  //                  line was already half-starved before this change.
  //                  Recorded in OPEN-FINDINGS; David rules whether CA needs
  //                  its panel wired up.
  //   Aviation       loses a button that did nothing.
  //
  // POST /quotations/:id/send-binding-app and the sheet both stay. This removes
  // a door, not a capability, and that endpoint is the re-send path when
  // servicing gets built.
  { key: 'snkl', label: 'Request SNKL signature', Icon: PenLine, alwaysVisible: true },
  // BOR: the Broker of Record letter, filled from the application, for the
  // client to sign on Tower (another agency holds the account at a market).
  { key: 'bor', label: 'Request BOR signature', Icon: PenLine, alwaysVisible: true },
  // LPR (Lost Policy Release) — only meaningful after the policy has been
  // bound. Visibility gated via SHOW_ON_STAGE below to keep it off pre-bind
  // surfaces. Spec: LPR-WIZARD-SPEC.md §4.1.
  { key: 'lpr', label: 'Draft LPR', Icon: FileSignature },
  { key: 'coi', label: 'Issue COI', Icon: FileSignature, alwaysVisible: true },
  { key: 'payment_request', label: 'Send payment request', Icon: Receipt },
  // Upload policy docs — drop a carrier policy PDF onto the deal (POST
  // /api/workdrive/upload). Until now the uploader only appeared on an
  // open_doc_drop Traffic Pattern task; this is the deal-level door. Post-bind
  // only, ordered just above Send the standard set so the flow reads upload, then send.
  { key: 'upload_docs', label: 'Upload policy docs', Icon: FileUp },
  // Send the standard set (was Publish documents, Gap 3; paperwork-1 A5 retired
  // publishing as a send path). Post-bind only, so gated via SHOW_ON_STAGE.
  // paperwork-1 A5 (L3-2): the action opens the standard set (the files attached to every recipient), no longer publish.
  { key: 'send_bundle', label: 'Send the standard set', Icon: FolderCheck },
  // 200 §2: THE BIND SWITCH. Records that the CARRIER bound it, which until now
  // had nowhere to live, so `bound` had to be inferred from money. It records;
  // it does not send. Shown on `binding` (a bind leg exists and no carrier
  // answer is recorded) and kept on the bound band, where a confirmation often
  // arrives days after the money did.
  { key: 'carrier_confirm', label: 'Record carrier confirmation', Icon: ShieldCheck },
  // Paperwork drafts (DEAL-LIFECYCLE-SPEC Phase 2): review pending bundle
  // drafts + regenerate. The ONLY door to client paper — approve files it.
  { key: 'paperwork', label: 'Review paperwork', Icon: FileSearch },
  // Edit roster — visible at EVERY stage (FLEET-ROSTER-SPEC.md Layer 2:
  // stage informs copy inside the sheet, it never gates rendering).
  { key: 'edit_roster', label: 'Edit roster', Icon: Plane, alwaysVisible: true },
  // Quote-drop diff — drop a carrier fleet-quote PDF, review the extracted
  // roster diff, apply through the same PUT /roster path (FLEET-ROSTER-SPEC.md
  // Layer 3). Visible at every stage like edit_roster.
  { key: 'quote_diff', label: 'Import quote PDF', Icon: FileSearch, alwaysVisible: true },
  // Edit named insured — rename the Account (§2.2). Available at every stage
  // (typo fixes, co-owner additions happen anytime); the sheet warns it
  // re-titles every term under the account.
  { key: 'edit_insured', label: 'Edit named insured', Icon: PenLine, alwaysVisible: true },
  // Edit policy info (TRACON-FIELD-GAP-INVENTORY.md, 2026-07-23): THE door
  // for Policy_Number, term dates, and Carrier on the deal. Kills the
  // "set it in Zoho" bounce the LPR/SNKL mappers used to force. Visible
  // from binding onward (a policy identity exists once binding starts);
  // also on cancelled/expired so late paperwork (LPRs, corrected certs)
  // can still fix the record.
  { key: 'policy_info', label: 'Edit policy info', Icon: PenLine },
  // Correct the bound liability limit from the carrier's document (David 2026-11-01: a $100K typed where the quote
  // said $200K). Bind, option, CRM and the signed application move together, with a reason (record-correction).
  { key: 'correct_limits', label: 'Correct liability limit', Icon: ShieldCheck },
  // Manage pilots — PILOT-DOORS Ship B (one door): opens the SAME RosterSheet
  // as edit_roster. There is ONE broker surface for pilot adds; this key
  // survives as a labeled affordance, not a separate process. Hidden
  // post-bound (the add route 409s on bound deals — that's an endorsement).
  { key: 'add_pilot', label: 'Manage pilots', Icon: UserPlus },
  // New deal from this policy (REWRITE-AND-CREDITS-SPEC Part A, generalized by
  // LAPSED-CLIENT-REUP-SPEC): clone the risk into a NEW new-business deal under
  // a different insured (entity change, ownership transfer) or the same one (a
  // client whose policy ended and wants coverage again). Meaningful only
  // post-bind, so gated to bound/placed/distributed/cancelled/expired via
  // STATE_ACTIONS.
  { key: 'rewrite', label: 'New deal from this policy', Icon: Replace },
  // Pilot changes since the quote (decision 8): information for David, never a
  // hold on the client's Sign (ruling 2026-10-19). The queue item is the
  // notice; this verb is the deal's own door to the same sheet, so a re-quote
  // note (a Waiting row with no button) can still be released. Binding only.
  { key: 'bind_hold', label: 'Review pilot changes', Icon: PauseCircle },
];

// DEAL-LIFECYCLE-SPEC Phase 1: HIDE_ON_STAGE is dead. Action visibility
// derives from the computed lifecycle state (deal.lifecycle_state, computed
// server-side by services/deal-state.service.js). One verb table, keyed on
// the derived state:
export const STATE_ACTIONS = {
  prospect:    new Set(['decline', 'quote', 'payment_request', 'add_pilot', 'welcome_text', 'resume_link', 'rtq']),
  shopping:    new Set(['decline', 'quote', 'payment_request', 'add_pilot', 'welcome_text', 'resume_link', 'rtq']),
  presented:   new Set(['decline', 'quote', 'payment_request', 'add_pilot']),
  binding:     new Set(['decline', 'quote', 'payment_request', 'policy_info', 'correct_limits', 'carrier_confirm', 'bind_hold']),
  // payment_request stays live post-bind: a mid-term endorsement invoice only
  // exists on a bound term (Got Wings, 2026-07-09). send_bundle (Publish
  // documents) stays on bound so existing bound deals keep their docs door;
  // Phase 4 refines the placed/distributed split.
  bound:       new Set(['renew', 'lpr', 'payment_request', 'upload_docs', 'send_bundle', 'paperwork', 'policy_info', 'correct_limits', 'rewrite', 'carrier_confirm']),
  placed:      new Set(['renew', 'lpr', 'payment_request', 'upload_docs', 'send_bundle', 'paperwork', 'policy_info', 'correct_limits', 'rewrite', 'carrier_confirm']),
  distributed: new Set(['renew', 'lpr', 'payment_request', 'upload_docs', 'send_bundle', 'paperwork', 'policy_info', 'correct_limits', 'rewrite', 'carrier_confirm']),
  lost:        new Set([]),
  cancelled:   new Set(['lpr', 'policy_info', 'rewrite']),
  expired:     new Set(['lpr', 'policy_info', 'rewrite']),
};

// THE one primary action per lifecycle state (MV-10 3.2, WHERE IS IT zone).
// One verb, accent-styled, rendered by the zone; every other verb folds into
// the secondary action bar. Defaults declared here (Ship 4a); David re-rules
// from the Monday punch list. Terminal states surface no primary — the
// stage badge popover carries the terminal doors.
export const PRIMARY_BY_STATE = {
  prospect:    'rtq',           // get it to markets
  shopping:    'quote',         // log what the carriers send back
  presented:   'send',          // move the client to a decision
  // No accent primary while binding: the signature is collected inside the
  // binding wizard, so there is nothing for a top-level button to do here.
  // `null` is already a supported value (see `lost`), not a new concept.
  binding:     null,
  bound:       'paperwork',     // review + file the client paper
  placed:      'send_bundle',   // deliver the documents
  distributed: 'renew',         // the term is served; look forward
  lost:        null,
  cancelled:   null,
  expired:     null,
};

export function primaryActionFor(lifecycleState, visibleActions) {
  const key = PRIMARY_BY_STATE[lifecycleState];
  if (!key) return null;
  return (visibleActions || []).find((a) => a.key === key) || null;
}

// Board cards don't carry lifecycle_state (it rides the focused deal
// payload only) — approximate it from the pipeline's derived stage so the
// kanban ⋯ menu agrees with the action bar.
export function lifecycleFromDerivedStage(stage) {
  switch (stage) {
    case 'LOST': return 'lost';
    case 'CANCELLED': return 'cancelled';
    case 'EXPIRED': return 'expired';
    case 'BOUND': case 'PAID': case 'RENEWAL_DUE': case 'NEEDS_ATTENTION': return 'bound';
    case 'BINDING': return 'binding';
    case 'PRESENTED': return 'presented';
    case 'UPCOMING': case 'INITIATED': case 'NEW_BUSINESS': return 'prospect';
    default: return 'shopping';
  }
}

function visibleActionsFor(lifecycleState) {
  if (!lifecycleState) return ALL_ACTIONS.filter((a) => a.alwaysVisible);
  const allowed = STATE_ACTIONS[lifecycleState] || new Set();
  return ALL_ACTIONS.filter(({ key, alwaysVisible }) => alwaysVisible || allowed.has(key));
}

// Accepts EITHER a dealId (account workspace path — fetches via useDeal)
// OR an already-loaded pipeline item (kanban-card path — avoids one
// network request per card). The two modes return the same shape.
export default function useDealActions(input) {
  const dealId =
    typeof input === 'string' || typeof input === 'number'
      ? input
      : input?.deal_id || input?.dealId || null;
  const itemFromCaller =
    typeof input === 'object' && input && (input.deal_id || input.dealId)
      ? input
      : null;
  // Skip the deal fetch when we already have the item (kanban-card
  // path). Hook ordering: useDeal MUST always be called for the rules
  // of hooks — pass null when we have the item so it no-ops internally.
  const fetchId = itemFromCaller ? null : dealId;
  const { data: dealRes } = useDeal(fetchId);
  const source = itemFromCaller || dealRes?.deal || null;
  const derivedStage = source?.stage || null;
  const crmStage = source?.deal_stage || null;
  // A pipeline item carries no session token (personal-logins): it is fetched for this one deal when
  // Compose opens. A focused deal's payload carries its own.
  const [fetchedToken, setFetchedToken] = useState(null);
  const sessionToken = itemFromCaller ? fetchedToken : (dealRes?.deal?.session_token || null);
  const loadToken = useCallback(async () => {
    if (!itemFromCaller) return;
    const t = await tokenForItem({ item: itemFromCaller, dealId, onSlow: toastNotice(toast) });
    if (!t.ok) toast.info(TOKEN_FAILED_WORDS);
    setFetchedToken(t.token);
  }, [itemFromCaller, dealId]);
  // Context bundle the Comms Hub recommender consumes (Vision §V2 #2).
  // Pipeline items carry days_until_expiration + days_since_last_activity
  // — surface them so the recommender escalates from "renewal-reminder"
  // into "non-renewal-notice" as time passes.
  const dealContext = {
    stage: crmStage,
    daysUntilExpiration: source?.days_until_expiration ?? null,
    daysSinceLastActivity: source?.days_since_last_activity ?? null,
  };

  const [composeOpen, setComposeOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  // Single quote sheet (merged Quick Log + Full Quote per Vision §V2
  // fix #6). Slot name kept as `quoteOpen` for the merged surface.
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [bindingOpen, setBindingOpen] = useState(false);
  const [snklOpen, setSnklOpen] = useState(false);
  const [borOpen, setBorOpen] = useState(false);
  const [lprOpen, setLprOpen] = useState(false);
  // Scoped-LPR hand-off (LPR-PARTIAL-CANCEL-AND-ROSTER-GATE-SPEC.md §5):
  // when the roster editor hits the paper-trail gate, it opens the LPR
  // sheet with { scope: 'aircraft', scope_tails } prefilled. Cleared when
  // the LPR sheet closes.
  const [lprPrefill, setLprPrefill] = useState(null);
  const [coiOpen, setCoiOpen] = useState(false);
  const [paymentRequestOpen, setPaymentRequestOpen] = useState(false);
  const [uploadDocsOpen, setUploadDocsOpen] = useState(false);
  const [sendBundleOpen, setSendBundleOpen] = useState(false);
  const [carrierConfirmOpen, setCarrierConfirmOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [quoteDiffOpen, setQuoteDiffOpen] = useState(false);
  const [editInsuredOpen, setEditInsuredOpen] = useState(false);
  // Policy info door (one component, also mounted inside LPRSheet).
  const [policyInfoOpen, setPolicyInfoOpen] = useState(false);
  const [limitCorrectionOpen, setLimitCorrectionOpen] = useState(false);
  // Set true when an Owner co-owner was just added, so the chained
  // EditNamedInsuredSheet opens pre-set to Partnership (David: co-ownership =
  // Partnership). Cleared when that sheet closes.
  const [coOwnerPartnership, setCoOwnerPartnership] = useState(false);
  // Compose hand-off slot — Save+Present on the quote sheet closes
  // the sheet then opens ComposeSheet with 'quotes-ready' pre-selected
  // (Vision §V2 fix #6 handoff decision 2026-05-20). Stored as the
  // initial slug so DealActionsSheets can use a single ComposeSheet
  // instance for both Send action and Save+Present hand-off.
  const [composeInitialSlug, setComposeInitialSlug] = useState(null);
  // Paperwork drafts review sheet (Phase 2).
  const [paperDraftsOpen, setPaperDraftsOpen] = useState(false);
  // Intake outreach sheets (MV-01 3.4/3.5, Ship 1b).
  const [welcomeTextOpen, setWelcomeTextOpen] = useState(false);
  const [resumeLinkOpen, setResumeLinkOpen] = useState(false);
  const [rtqOpen, setRtqOpen] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [bindHoldOpen, setBindHoldOpen] = useState(false);

  // Lifecycle state: the focused deal payload carries the server-computed
  // state; kanban items approximate it from the derived stage.
  const lifecycleState = source?.lifecycle_state?.state
    || lifecycleFromDerivedStage(derivedStage);

  const actions = visibleActionsFor(lifecycleState);

  const openers = {
    send: useCallback(async () => {
      await loadToken();
      setComposeInitialSlug(null);
      setComposeOpen(true);
    }, [loadToken]),
    decline: useCallback(() => setDeclineOpen(true), []),
    quote: useCallback(() => setQuoteOpen(true), []),
    paperwork: useCallback(() => setPaperDraftsOpen(true), []),
    renew: useCallback(() => setRenewOpen(true), []),
    binding: useCallback(() => setBindingOpen(true), []),
    snkl: useCallback(() => setSnklOpen(true), []),
    bor: useCallback(() => setBorOpen(true), []),
    lpr: useCallback(() => setLprOpen(true), []),
    coi: useCallback(() => setCoiOpen(true), []),
    payment_request: useCallback(() => setPaymentRequestOpen(true), []),
    upload_docs: useCallback(() => setUploadDocsOpen(true), []),
    send_bundle: useCallback(() => setSendBundleOpen(true), []),
    carrier_confirm: useCallback(() => setCarrierConfirmOpen(true), []),
    edit_roster: useCallback(() => setRosterOpen(true), []),
    quote_diff: useCallback(() => setQuoteDiffOpen(true), []),
    edit_insured: useCallback(() => setEditInsuredOpen(true), []),
    policy_info: useCallback(() => setPolicyInfoOpen(true), []),
    correct_limits: useCallback(() => setLimitCorrectionOpen(true), []),
    // PILOT-DOORS Ship B: Manage pilots and Edit roster are the SAME door.
    add_pilot: useCallback(() => setRosterOpen(true), []),
    welcome_text: useCallback(() => setWelcomeTextOpen(true), []),
    resume_link: useCallback(() => setResumeLinkOpen(true), []),
    rtq: useCallback(() => setRtqOpen(true), []),
    rewrite: useCallback(() => setRewriteOpen(true), []),
    // Decision 8: the signing hold sheet (BindHoldSheet). Also the ?door=
    // bind_hold deep link, the bind_held queue item and the AIRgus remedy.
    bind_hold: useCallback(() => setBindHoldOpen(true), []),
  };

  // Pipeline-item payload passed through to InitiateRenewalDialog so
  // it can branch on item.renewal_readiness without a separate fetch.
  // null when useDealActions was called with just a dealId (no item
  // in hand) — the dialog handles that gracefully.
  const itemSnapshot = itemFromCaller || null;

  // Helper for the QuoteEntrySheet Save+Present hand-off — closes
  // the quote sheet (caller does that via onOpenChange) and asks the
  // shared ComposeSheet to open pre-selected to 'quotes-ready'.
  const openComposeWithTemplate = useCallback(async (slug) => {
    await loadToken();
    setComposeInitialSlug(slug);
    setComposeOpen(true);
  }, [loadToken]);

  return {
    derivedStage,
    lifecycleState,
    crmStage,
    sessionToken,
    dealContext,
    // Source item (either the caller-provided pipeline item OR the
    // deal payload fetched by useDeal). Surfaced so the renewal
    // dialog can read renewal_readiness / policy_expiration /
    // named_insured without a second fetch.
    item: itemSnapshot || source,
    actions,
    openers,
    openComposeWithTemplate,
    sheetState: {
      composeOpen,
      setComposeOpen,
      composeInitialSlug,
      setComposeInitialSlug,
      declineOpen,
      setDeclineOpen,
      quoteOpen,
      setQuoteOpen,
      renewOpen,
      setRenewOpen,
      bindingOpen,
      setBindingOpen,
      snklOpen,
      setSnklOpen,
      borOpen,
      setBorOpen,
      lprOpen,
      setLprOpen,
      lprPrefill,
      setLprPrefill,
      coiOpen,
      setCoiOpen,
      paymentRequestOpen,
      setPaymentRequestOpen,
      uploadDocsOpen,
      setUploadDocsOpen,
      sendBundleOpen,
      setSendBundleOpen,
      carrierConfirmOpen,
      setCarrierConfirmOpen,
      rosterOpen,
      setRosterOpen,
      quoteDiffOpen,
      setQuoteDiffOpen,
      editInsuredOpen,
      setEditInsuredOpen,
      policyInfoOpen,
      setPolicyInfoOpen,
      limitCorrectionOpen,
      setLimitCorrectionOpen,
      coOwnerPartnership,
      setCoOwnerPartnership,
      paperDraftsOpen,
      setPaperDraftsOpen,
      welcomeTextOpen,
      setWelcomeTextOpen,
      resumeLinkOpen,
      setResumeLinkOpen,
      rtqOpen,
      setRtqOpen,
      rewriteOpen,
      setRewriteOpen,
      bindHoldOpen,
      setBindHoldOpen,
    },
  };
}
